'use client';
import type {
  GroupedNavItem,
  NavigationClassNamesType,
  NavigationItem,
  NavigationPropsType,
  NavigationStylesType,
} from '@unifyapps/carbon/components/NavigationContainer/types';
import { useRouter } from '@unifyapps/carbon/navigation';
import { CUSTOM_NAVIGATION_CONTAINER } from '@unifyapps/carbon/no-code/const';
import { InterfaceModes, useInterfaceStore } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { safeJsonStringify } from '@unifyapps/carbon/utils/json';
import { NavigationPosition } from '@unifyapps/defs/types/interface';
import { useTranslation } from '@unifyapps/i18n/client';
import { Box } from '@unifyapps/ui/components/Box';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import ContainerElementContext from '@unifyapps/ui/providers/ContainerElementContext';
import clsx from 'clsx';
import noop from 'lodash/noop';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BlockComponentOverridesProvider,
  DEFAULT_BLOCK_COMPONENT_OVERRIDES,
} from '../../context/BlockComponentOverridesContext';
import { hasUnresolvedDynamicPathParams } from '../../utils/path/interfacePathDynamicParamsHandlers';
import { Module } from '../Module';

/**
 * External URLs can cause Next.js router.prefetch errors.
 * So we only prefetch internal links.
 */
const isSameOriginPath = (url: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const parsed = new URL(url.trim(), window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

function collectNavigationPrefetchUrls(items: NavigationItem[]): string[] {
  const urls = new Set<string>();

  function getAllUrls(navItem: NavigationItem | GroupedNavItem): void {
    if (navItem.type === 'GROUP') {
      for (const child of navItem.items) {
        getAllUrls(child);
      }
      return;
    }
    if (navItem.type === 'URL' && navItem.url && isSameOriginPath(navItem.url)) {
      urls.add(navItem.url);
      return;
    }
    if (navItem.type === 'APPLICATION_PAGE' && navItem.url && isSameOriginPath(navItem.url)) {
      urls.add(navItem.url);
    }
  }

  for (const item of items) {
    getAllUrls(item);
  }

  return Array.from(urls);
}

const SLOTS = { loading: Box };

export const DATA_NAV_POSITION_ATTRIBUTE = 'data-nav-position';

export type CustomNavigationProps = {
  moduleId: string;
  interfaceRecordId: string;
  navigation: NavigationPropsType['navigation'];
  navigationStyles: NavigationStylesType;
  navigationClassNames: NavigationClassNamesType;
  position: NavigationPosition;
  showPreview?: boolean;
};

/**
 * Renders the custom web navigation module. Shared by platform (builder/preview) and matrix (runner).
 * In builder mode, pointer events are disabled so the canvas remains interactive.
 */
export function CustomNavigation({
  moduleId,
  interfaceRecordId,
  navigation,
  navigationStyles,
  navigationClassNames,
  position,
  showPreview,
}: CustomNavigationProps) {
  const interfaceStore = useInterfaceStore();
  const mode = interfaceStore.hooks.useMode();
  const isBuilderMode = mode === InterfaceModes.BUILDER;
  const isPreviewMode = mode === InterfaceModes.PREVIEW;
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['interfaces-matrix']);

  // Tracks which URLs have already been prefetched to avoid duplicate prefetch calls.
  const prefetchedUrls = useRef(new Set<string>());

  const router = useRouter();

  // Will be used to set the container element for the custom navigation.
  const [basePageEl, setBasePageEl] = useState<HTMLDivElement | null>(null);

  const { widthClassName, heightClassName } = navigationClassNames;
  const { widthStyles, heightStyles } = navigationStyles;

  const providersKey = useMemo(() => {
    const primaryNavItems = navigation.primary;
    const secondaryNavItems = navigation.secondary;
    const navItemsHash =
      primaryNavItems || secondaryNavItems
        ? safeJsonStringify(primaryNavItems?.map((item) => item.id) ?? [], '') +
          safeJsonStringify(secondaryNavItems?.map((item) => item.id) ?? [], '')
        : '';
    return `${moduleId}-${navItemsHash}`;
  }, [moduleId, navigation]);

  const { className, style } = useMemo(() => {
    if (position === NavigationPosition.Start) {
      return {
        className: clsx(widthClassName, 'h-full'),
        style: widthStyles,
      };
    }
    return {
      className: clsx(heightClassName, 'w-full'),
      style: heightStyles,
    };
  }, [position, widthClassName, widthStyles, heightClassName, heightStyles]);

  const slotProps = useMemo(() => {
    return {
      loading: {
        className: className,
        style: style,
      },
    };
  }, [className, style]);

  const attributesAndEventListeners = useMemo(() => {
    return {
      [DATA_NAV_POSITION_ATTRIBUTE]: position,
      onClickCapture: (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a');
        if (anchor) {
          const href = anchor.getAttribute('href');
          if (href) {
            const isInvalid = hasUnresolvedDynamicPathParams(href);

            if (isInvalid) {
              e.preventDefault();
              e.stopPropagation();
              console.error('Dynamic Params not passed properly', href);
              if (isPreviewMode) {
                showSnackbar({
                  color: 'error',
                  title: t('interfaces-matrix:appActions.DynamicParamsNotPassedProperly'),
                });
              }
            }
          }
        }
      },
    };
  }, [isPreviewMode, position, showSnackbar, t]);

  // Get all the urls from the navigation items to prefetch.
  // To prevent the complete load of page when clicking on some block
  // with Go to page action.
  const urlsToPrefetch = useMemo(() => {
    if (isBuilderMode) {
      return [];
    }
    const mergedNavigationItems = [...(navigation.primary ?? []), ...(navigation.secondary ?? [])];
    return collectNavigationPrefetchUrls(mergedNavigationItems);
  }, [navigation.primary, navigation.secondary, isBuilderMode]);

  useEffect(() => {
    urlsToPrefetch.forEach((url) => {
      if (!prefetchedUrls.current.has(url)) {
        router.prefetch(url);
        prefetchedUrls.current.add(url);
      }
    });
  }, [urlsToPrefetch, router]);

  return (
    <BlockComponentOverridesProvider overrides={DEFAULT_BLOCK_COMPONENT_OVERRIDES}>
      <ContainerElementContext.Provider value={basePageEl}>
        <Module
          attributesAndEventListeners={attributesAndEventListeners}
          className={clsx(className, CUSTOM_NAVIGATION_CONTAINER, {
            'pointer-events-none': isBuilderMode && !showPreview,
            relative: position === NavigationPosition.Top,
          })}
          key={providersKey}
          moduleId={moduleId}
          moduleInterfaceRecordId={interfaceRecordId}
          onPageScopeKey={noop}
          setWrapperRef={setBasePageEl}
          slotProps={slotProps}
          slots={SLOTS}
          style={style}
        />
      </ContainerElementContext.Provider>
    </BlockComponentOverridesProvider>
  );
}
